import React from 'react';
import { Box, Typography, Paper, Chip, Fade, keyframes } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchIcon from '@mui/icons-material/Search';
import PsychologyIcon from '@mui/icons-material/Psychology';
import TuneIcon from '@mui/icons-material/Tune';
import DescriptionIcon from '@mui/icons-material/Description';
import ReactMarkdown from 'react-markdown';

export interface StatusEvent {
  type: string;
  tool_name?: string;
  label?: string;
  /** Supporting detail, e.g. the phrase being searched for. */
  detail?: string;
  /** An earlier step the agent has moved past. */
  done?: boolean;
  /** Current step has been running a while — reassure rather than look frozen. */
  stalled?: boolean;
}

interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  statusEvents?: StatusEvent[];
}

// Dot animation keyframes
const dot1 = keyframes`
  0%, 20% { opacity: 0; }
  30%, 100% { opacity: 1; }
`;
const dot2 = keyframes`
  0%, 40% { opacity: 0; }
  50%, 100% { opacity: 1; }
`;
const dot3 = keyframes`
  0%, 60% { opacity: 0; }
  70%, 100% { opacity: 1; }
`;
const dotFade = keyframes`
  0%, 70% { opacity: 1; }
  80%, 100% { opacity: 0; }
`;

const BouncingDots: React.FC = () => (
  <Box component="span" sx={{ display: 'inline-flex', gap: '2px', ml: 0.5, verticalAlign: 'middle' }}>
    {[dot1, dot2, dot3].map((anim, i) => (
      <Box
        key={i}
        component="span"
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          backgroundColor: 'text.secondary',
          display: 'inline-block',
          animation: `${anim} 1.4s ease-in-out infinite, ${dotFade} 1.4s ease-in-out infinite`,
          animationDelay: `${i * 0.2}s`,
        }}
      />
    ))}
  </Box>
);

const getStatusIcon = (label?: string) => {
  if (!label) return <PsychologyIcon sx={{ fontSize: 14 }} />;
  const lower = label.toLowerCase();
  if (lower.includes('search')) return <SearchIcon sx={{ fontSize: 14 }} />;
  if (lower.includes('found') || lower.includes('reading the')) return <DescriptionIcon sx={{ fontSize: 14 }} />;
  if (lower.includes('working out') || lower.includes('looking for')) return <TuneIcon sx={{ fontSize: 14 }} />;
  return <PsychologyIcon sx={{ fontSize: 14 }} />;
};

const ChatMessage: React.FC<ChatMessageProps> = ({ role, content, isStreaming, statusEvents }) => {
  const isUser = role === 'user';

  const steps = statusEvents ?? [];
  const showStatus = isStreaming && steps.length > 0 && !content;

  return (
    <Box sx={{
      display: 'flex',
      gap: 1.5,
      mb: 2,
      flexDirection: isUser ? 'row-reverse' : 'row',
      alignItems: 'flex-start',
    }}>
      {/* Avatar */}
      <Box sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isUser ? 'primary.main' : 'secondary.main',
        color: 'white',
        flexShrink: 0,
        mt: 0.5,
      }}>
        {isUser ? <PersonIcon sx={{ fontSize: 20 }} /> : <AutoAwesomeIcon sx={{ fontSize: 20 }} />}
      </Box>

      {/* Message area */}
      <Box sx={{ maxWidth: '75%', minWidth: 0 }}>
        {/* Progress steps — reserved space while streaming, before any content arrives */}
        {isStreaming && !content && (
          <Box sx={{ height: 160 }}>
            {showStatus && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {steps.map((step, i) => {
                  const isCurrent = i === steps.length - 1;
                  return (
                    <Fade in={true} timeout={300} key={`${step.label}-${i}`}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip
                          size="small"
                          icon={getStatusIcon(step.label)}
                          label={
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                              {step.label}
                              {step.detail && (
                                <Box
                                  component="span"
                                  sx={{
                                    ml: 0.75,
                                    maxWidth: 260,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    opacity: 0.8,
                                  }}
                                >
                                  {step.detail}
                                </Box>
                              )}
                              {isCurrent && <BouncingDots />}
                            </Box>
                          }
                          sx={{
                            fontSize: '0.75rem',
                            height: 28,
                            maxWidth: '100%',
                            backgroundColor: isCurrent ? 'rgba(58, 80, 107, 0.08)' : 'transparent',
                            color: 'secondary.main',
                            border: isCurrent
                              ? '1px solid rgba(58, 80, 107, 0.12)'
                              : '1px solid transparent',
                            opacity: isCurrent ? 1 : 0.45,
                            transition: 'opacity 0.3s ease, background-color 0.3s ease',
                            '& .MuiChip-icon': { color: 'secondary.main' },
                            '& .MuiChip-label': { overflow: 'hidden' },
                          }}
                        />
                      </Box>
                    </Fade>
                  );
                })}
                {steps[steps.length - 1]?.stalled && (
                  <Typography
                    variant="caption"
                    sx={{ pl: 1.5, color: 'text.secondary', opacity: 0.7, fontStyle: 'italic' }}
                  >
                    Still working — larger records take a little longer
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Main content bubble */}
        {content && (
          <Fade in={true} timeout={300}>
            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1.5,
                backgroundColor: isUser ? 'primary.main' : '#f0f2f5',
                color: isUser ? 'white' : 'text.primary',
                borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              }}
            >
              <Box sx={{
                '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
                '& ul, & ol': { pl: 2.5, mb: 1 },
                '& li': { mb: 0.5 },
                '& code': {
                  backgroundColor: isUser ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)',
                  px: 0.5,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontSize: '0.85em',
                },
                '& pre': {
                  backgroundColor: isUser ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
                  p: 1.5,
                  borderRadius: 1,
                  overflow: 'auto',
                  '& code': { backgroundColor: 'transparent', p: 0 },
                },
                '& strong': { fontWeight: 600 },
                fontSize: '0.95rem',
                lineHeight: 1.6,
              }}>
                <ReactMarkdown>{content}</ReactMarkdown>
              </Box>
            </Paper>
          </Fade>
        )}

      </Box>
    </Box>
  );
};

export default ChatMessage;
